import { Link } from '@inertiajs/react'
import { Calendar, Pin } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent } from '@/components/ui/card'
import ReactionBar from '@/components/ReactionBar'
import type { FeedOrgPost } from '@/types'

type Props = { post: FeedOrgPost; canReact: boolean }

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text
}

function formatDate(iso: string): string {
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso)
  const d = new Date(isDateOnly ? `${iso}T12:00:00` : iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function OrgPostFeedItem({ post, canReact }: Props) {
  const excerpt = truncate(stripHtml(post.body), 280)
  return (
    <Card className="overflow-hidden">
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center gap-3">
          <Link href={`/o/${post.organization.slug}`} className="shrink-0">
            <Avatar className="h-10 w-10">
              {post.organization.logo_url && (
                <AvatarImage src={post.organization.logo_url} alt={post.organization.name} />
              )}
              <AvatarFallback>{post.organization.name[0]}</AvatarFallback>
            </Avatar>
          </Link>
          <div className="min-w-0 flex-1">
            <Link
              href={`/o/${post.organization.slug}`}
              className="font-semibold text-foreground hover:underline"
            >
              {post.organization.name}
            </Link>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {post.pinned_at && (
                <span className="inline-flex items-center gap-1 rounded-md border border-primary/20 bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                  <Pin className="size-2.5" />
                  Pinned
                </span>
              )}
              <span>{formatDate(post.created_at)}</span>
              {post.edited_at && (
                <>
                  <span>·</span>
                  <span>edited</span>
                </>
              )}
            </div>
          </div>
        </div>

        {post.image_url && (
          <img
            src={post.image_url}
            alt=""
            loading="lazy"
            className="aspect-[16/9] w-full rounded-lg object-cover ring-1 ring-black/5"
          />
        )}

        <p className="text-base text-foreground">{excerpt}</p>

        {post.linked_event && (
          <Link
            href={`/events/${post.linked_event.slug}`}
            className="inline-flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm ring-1 ring-black/5 transition hover:bg-muted hover:ring-primary/20"
          >
            <Calendar className="size-4 shrink-0 text-primary" />
            <span className="truncate font-medium text-foreground">{post.linked_event.title}</span>
            {post.linked_event.date && (
              <span className="shrink-0 text-muted-foreground">
                · {formatDate(post.linked_event.date)}
              </span>
            )}
          </Link>
        )}

        <ReactionBar
          postId={post.id}
          reactionsSummary={post.reactions_summary}
          currentUserReaction={post.current_user_reaction}
          canReact={canReact}
        />
      </CardContent>
    </Card>
  )
}
